import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { catalogueReducer } from '../../store/catalogue/catalogue.reducer';
import {
  getCatalogueIsLoading,
  getSelectedProduct,
  getSelectedSeries,
} from '../../store/catalogue/catalogue.selectors';
import { LoadingSpinner } from '../loading/LoadingSpinner';
import AddProductModal from './AddProductModal';
import DeleteConfirmModal from './DeleteConfirmModal';

const { selectProduct, createProduct, modifyProduct, removeProduct } = catalogueReducer.actions;

interface EditProductData {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
}

interface CatalogueProductSidebarProps {
  canEdit?: boolean;
}

const CatalogueProductSidebar: React.FC<CatalogueProductSidebarProps> = ({ canEdit = true }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedSeries = useAppSelector(getSelectedSeries);
  const selectedProduct = useAppSelector(getSelectedProduct);
  const isLoading = useAppSelector(getCatalogueIsLoading);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [hoveredProductId, setHoveredProductId] = useState<string | null>(null);
  const [editProduct, setEditProduct] = useState<EditProductData | null>(null);
  const [deletingProductData, setDeletingProductData] = useState<{ id: string; name: string } | null>(null);

  if (!selectedSeries) {
    return null;
  }

  const handleProductClick = (productId: string) => {
    dispatch(selectProduct(selectedProduct?.id === productId ? null : productId));
  };

  const handleAddProduct = (name: string, description?: string, imageBase64?: string) => {
    dispatch(createProduct({ seriesId: selectedSeries.id, name, description, imageBase64 }));
  };

  const handleEditProduct = (e: React.MouseEvent, product: EditProductData) => {
    e.stopPropagation();
    setEditProduct(product);
  };

  const handleDeleteProduct = (e: React.MouseEvent, product: { id: string; name: string }) => {
    e.stopPropagation();
    setDeletingProductData(product);
  };

  const handleUpdateProduct = (name: string, description?: string, imageBase64?: string) => {
    if (editProduct) {
      dispatch(modifyProduct({ id: editProduct.id, data: { name, description, imageBase64 } }));
      setEditProduct(null);
    }
  };

  const handleConfirmDelete = () => {
    if (deletingProductData) {
      dispatch(removeProduct(deletingProductData.id));
    }
  };

  return (
    <div
      className="flex flex-col flex-1 min-h-0 bg-black border-r border-zinc-800"
      data-testid="catalogue-product-sidebar"
    >
      {/* Products Header with Add Button */}
      <div
        className="flex items-center justify-between w-full px-4 text-zinc-500 text-sm border-b border-zinc-800"
        style={{
          height: '45px',
        }}
      >
        <span>{t('catalogue.productsArticles')}</span>
        {canEdit && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="p-1 hover:bg-greyP4 rounded text-green01 hover:text-green03 transition-colors"
            title={t('catalogue.addProduct')}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Products List */}
      <div
        className="flex flex-col items-start px-2 py-2 flex-1 overflow-y-auto"
        style={{
          gap: '2px',
        }}
      >
        {isLoading ? (
          <div className="flex justify-center items-center w-full py-8">
            <LoadingSpinner size={24} color="#B3EC49" />
          </div>
        ) : (
          <>
            {selectedSeries.products.map((product) => (
              <div
                key={product.id}
                className="relative w-full"
                onMouseEnter={() => setHoveredProductId(product.id)}
                onMouseLeave={() => setHoveredProductId(null)}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleProductClick(product.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProductClick(product.id);
                    }
                  }}
                  className={`flex items-center w-full text-sm transition-colors duration-200 h-[40px] cursor-pointer
                ${selectedProduct?.id === product.id ? 'text-white' : 'text-greyP2 hover:text-white hover:bg-greyP4'}`}
                  style={
                    selectedProduct?.id === product.id
                      ? {
                          background: '#232323',
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'stretch',
                        }
                      : {
                          borderRadius: '6px',
                          padding: '10px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          alignSelf: 'stretch',
                        }
                  }
                >
                  <span className="truncate flex-1 text-left" title={product.name}>
                    {product.name}
                  </span>
                  {canEdit && hoveredProductId === product.id && (
                    <div className="flex items-center gap-1 mr-1">
                      <button
                        onClick={(e) => handleEditProduct(e, product)}
                        className="p-1 hover:bg-white/10 rounded"
                        title={t('common.edit')}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => handleDeleteProduct(e, product)}
                        className="p-1 hover:bg-white/10 rounded text-red-400"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {product.variants.length > 0 && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                </div>
              </div>
            ))}

            {selectedSeries.products.length === 0 && (
              <div className="px-4 py-8 text-center w-full flex flex-col items-center gap-4">
                <p className="text-greyP3 text-sm">{t('catalogue.noProducts')}</p>
                {canEdit && (
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-green01 text-black text-sm font-medium
                  hover:bg-green03 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('catalogue.addProduct')}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      <AddProductModal
        isOpen={isAddModalOpen || !!editProduct}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditProduct(null);
        }}
        onAddProduct={editProduct ? handleUpdateProduct : handleAddProduct}
        seriesName={selectedSeries.name}
        editData={
          editProduct
            ? { name: editProduct.name, description: editProduct.description, imageUrl: editProduct.imageUrl }
            : null
        }
      />

      {/* Delete Product Confirm Modal */}
      <DeleteConfirmModal
        isOpen={!!deletingProductData}
        onClose={() => setDeletingProductData(null)}
        onConfirm={handleConfirmDelete}
        title={t('catalogue.deleteProductTitle')}
        itemName={deletingProductData?.name || ''}
        itemType="product"
      />
    </div>
  );
};

export default CatalogueProductSidebar;
